import React from 'react';
import { CITIZENSHIP_STATUS } from './constants';

const Summary = ({ person, onNextSteps, onViewChecklist }) => {
  if (!person) return null;

  const isCalculated = person.calculatedStatus !== null;
  const hasAncestors = person.ancestors && person.ancestors.length > 0;
  const needsCalculation = hasAncestors && !isCalculated && person.isCitizen === CITIZENSHIP_STATUS.NO;

  const effectiveStatus = person.calculatedStatus ?? person.isCitizen;
  const isEligible = [
    CITIZENSHIP_STATUS.BORN_IN_CANADA,
    CITIZENSHIP_STATUS.BY_DESCENT,
    CITIZENSHIP_STATUS.NATURALIZED
  ].includes(effectiveStatus);

  const name = person.name || "The Applicant";

  const getApplyingList = (p, gen = 1) => {
    let list = [];
    if (p.ancestors) {
      p.ancestors.forEach(a => {
        const aEffectiveStatus = a.calculatedStatus ?? a.isCitizen;
        const aIsEligible = [
          CITIZENSHIP_STATUS.BORN_IN_CANADA,
          CITIZENSHIP_STATUS.BY_DESCENT,
          CITIZENSHIP_STATUS.NATURALIZED
        ].includes(aEffectiveStatus);

        if (a.alsoApplying && aIsEligible) {
          let label = gen === 1 ? "Parent" : (gen === 2 ? "Grandparent" : `${'Great-'.repeat(gen - 2)}Grandparent`);
          list.push({ ...a, defaultLabel: label });
        }
        list = list.concat(getApplyingList(a, gen + 1));
      });
    }
    return list;
  };

  const applyingAncestors = getApplyingList(person);
  const othersApplying = applyingAncestors.length > 0;
  const applyingNames = applyingAncestors.map(p => p.name || `The ${p.defaultLabel}`).join(", ");

  const isHappyOutcome = isEligible || othersApplying;
  const isUncertain = effectiveStatus === 'NOT_SURE';

  let bgColor = '#f3f4f6';
  let borderColor = '#d1d5db';

  if (needsCalculation) {
    bgColor = '#eff6ff';
    borderColor = '#bfdbfe';
  } else if (hasAncestors) {
    if (isHappyOutcome) {
      bgColor = '#f0fdf4';
      borderColor = '#86efac';
    } else if (isUncertain) {
      bgColor = '#fefce8';
      borderColor = '#fde047';
    } else {
      bgColor = '#fef2f2';
      borderColor = '#fca5a5';
    }
  }

  return (
    <div style={{
      backgroundColor: bgColor,
      border: `1px solid ${borderColor}`,
      borderRadius: '12px',
      padding: '24px',
      margin: '20px auto',
      maxWidth: '800px',
      textAlign: 'left',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    }}>
      <h2 style={{ marginTop: 0, color: '#111827' }}>
        {needsCalculation
          ? "Let's Calculate Eligibility"
          : (hasAncestors ? (isHappyOutcome ? `🇨🇦 Great news, eh!` : (isUncertain ? `🤔 More info needed` : `🍁 We're sorry to say...`)) : `👋 Welcome to the Citizenship Checker`)}
      </h2>

      {!hasAncestors && (
        <div style={{ backgroundColor: '#f9fafb', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <p style={{ marginTop: 0, fontWeight: '600' }}>How to use this tool:</p>
          <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px', lineHeight: '1.6' }}>
             <li><strong>Build your tree:</strong> Click the <span style={{ color: '#2563eb', fontWeight: '600' }}>+ Add Parent</span> button on the Applicant's card below.</li>
             <li><strong>Go back in time:</strong> Continue adding parents until you reach an ancestor who was born or naturalized in Canada.</li>
             <li><strong>Check status:</strong> Click "Calculate Eligibility" to see if citizenship was passed down.</li>
          </ol>
        </div>
      )}

      {needsCalculation && (
        <p>You have added ancestors to your tree. Please click the <strong>Calculate Eligibility</strong> button above to determine your status.</p>
      )}

      {hasAncestors && !needsCalculation && (
        <div>
          {isUncertain ? (
            <div>
              <p style={{ fontSize: '1.1rem' }}>We cannot determine <strong>{name}'s</strong> eligibility yet. Please ensure all ancestors have their Citizenship Status and Dates entered correctly.</p>
            </div>
          ) : isEligible ? (
            <div>
              <p style={{ fontSize: '1.1rem' }}>It looks like <strong>{name}</strong> is likely <strong>eligible</strong> for Citizenship By Descent!</p>
              {othersApplying && (
                <p style={{ marginTop: '8px', color: '#000000' }}>
                  <strong>Also</strong> It looks like <strong>{applyingNames}</strong> {applyingAncestors.length > 1 ? 'are' : 'is'}  likely eligible for proof of citizenship.
                </p>
              )}
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '1.1rem' }}>Based on the current laws, <strong>{name}</strong> is likely <strong>not eligible</strong> for Citizenship By Descent.</p>
              <p style={{ fontSize: '0.95rem', color: '#6b7280', fontStyle: 'italic' }}>
                <strong>Note:</strong> Ensure you have added all relevant ancestors (parents, grandparents, great-grandparents) to the tree before considering this result final.
              </p>
              {othersApplying ? (
                <div style={{ marginTop: '12px', marginBottom: '16px', padding: '12px', backgroundColor: '#dcfce7', borderRadius: '8px', border: '1px solid #86efac', color: '#14532d' }}>
                  <p style={{ margin: 0 }}>
                    However, <strong>{applyingNames}</strong> {applyingAncestors.length > 1 ? 'appear' : 'appears'} to be eligible and {applyingAncestors.length > 1 ? 'are' : 'is'} applying!
                  </p>
                  <p style={{ margin: '8px 0 0 0', fontSize: '0.95rem' }}>
                    <strong>Why?</strong> While {name} may not qualify (likely due residency requirements not being met by the parent), the ancestors can still reclaim their status.
                  </p>
                </div>
              ) : (
                <>
                  <p>We know this might be disappointing. However, your Canadian journey may not end here!</p>
                  <p><strong>Alternative Pathways:</strong></p>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>Explore <a href="https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada.html" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>Permanent Residency</a> options like Express Entry.</li>
                  </ul>
                </>
              )}
            </div>
          )}

          {(isEligible || othersApplying) && (
            <>
              <p>Let's go bud!</p>
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px', marginBottom: '16px' }}>
                <button
                  onClick={onViewChecklist}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#ffffff',
                    color: '#2563eb',
                    border: '1px solid #2563eb',
                    borderRadius: '6px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Go to Full Document Page
                </button>
                <button
                  onClick={onNextSteps}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#ffffff',
                    color: '#2563eb',
                    border: '1px solid #2563eb',
                    borderRadius: '6px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  How to Apply
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Summary;